/**
 * A custom hook-like function to manage video playback,
 * focusing on providing a robust autoplay feature with fallbacks and advanced options.
 */
export const useVideo = () => {
    /**
     * Replaces a video element with an image element
     * used if the video is not supported.
     *
     * @param {HTMLVideoElement} video - The video element to replace.
     */
    const replaceVideoWithImage = (video) => {
        if (!video || video.dataset.replaced === "true") return;
        video.dataset.replaced = "true";

        // stop playback/download to save bandwidth.
        try {
            video.pause();
        } catch {}
        video.removeAttribute("src");
        video.querySelectorAll("source").forEach((s) => s.remove());
        video.load();

        // prefer explicit alt, fall back to nested <img alt> or a generic label.
        const childAlt = video.querySelector("img[alt]")?.getAttribute("alt");
        const alt =
            video.getAttribute("data-fallback-alt") ||
            childAlt ||
            "Banner background";

        const hasImgSibling = Boolean(
            video.previousElementSibling &&
                video.previousElementSibling.tagName === "IMG"
        );
        if (video.poster && !hasImgSibling) {
            const img = document.createElement("img");
            img.src = video.poster;
            img.alt = alt;
            img.className = video.className;
            img.style.cssText = video.style.cssText;
            video.parentNode?.insertBefore(img, video);
        }
        video.style.display = "none";
    };

    /**
     * Wait until all videos matching a selector can play.
     * Returns a Promise that resolves when all videos are ready.
     *
     * @param {object} [options] - Configuration options.
     * @param {string} [options.selector="video"] - The CSS selector for the video elements.
     * @returns {Promise<HTMLVideoElement[]>}
     */
    const canplay = (options = { selector: "video" }) => {
        const _options = { selector: "video", ...options };
        const videos = Array.from(document.querySelectorAll(_options.selector));

        if (videos.length === 0) {
            return Promise.resolve([]);
        }

        const canPlayPromises = videos.map(
            (video) =>
                new Promise((resolve) => {
                    if (video.readyState >= 3) {
                        resolve(video); // déjà prêt
                    } else {
                        video.addEventListener(
                            "canplay",
                            () => resolve(video),
                            { once: true }
                        );
                    }
                })
        );

        return Promise.all(canPlayPromises);
    };

    /**
     * Attempts to autoplay all videos matching a CSS selector.
     * If autoplay fails, it falls back to the video's poster image.
     * It also supports a delayed loop feature.
     *
     * @param {object} [options] - Configuration options.
     * @param {string} [options.selector="video"] - The CSS selector for the video elements.
     * @param {number} [options.timeout=1000] - The delay in ms for the fallback check.
     */
    const autoplay = (options = { selector: "video", timeout: 1000 }) => {
        // Define default options to ensure the function always has valid values.
        const _defaultOptions = {
            selector: "video",
            timeout: 1000,
        };
        // Merge user-provided options with the defaults. User options will override defaults.
        const _options = { ..._defaultOptions, ...options };

        // Select all video elements using the configured selector.
        const videos = document.querySelectorAll(_options.selector);

        for (const video of videos) {
            // Set essential properties for autoplay.
            video.muted = true;
            video.playsInline = true;
            video.autoplay = true;

            // Attempt to play the video.
            const playPromise = video.play();

            const timeoutId = setTimeout(() => {
                if (video.paused) {
                    replaceVideoWithImage(video);
                }
            }, _options.timeout);

            if (playPromise !== undefined) {
                playPromise
                    .then(() => clearTimeout(timeoutId))
                    .catch(() => {
                        clearTimeout(timeoutId);
                        replaceVideoWithImage(video);
                    });
            }

            // Get the pause duration from the video's `data-video-pause` attribute.
            const pauseDurationAttr = video.dataset.videoPause;
            if (pauseDurationAttr) {
                // ...convert its value (a string) to an integer.
                const pauseMs = parseInt(pauseDurationAttr, 10);
                // If the result is a valid, positive number...
                if (!isNaN(pauseMs) && pauseMs > 0) {
                    // Listen for the 'ended' event, which fires when the video finishes playing.
                    video.addEventListener("ended", function handlePauseLoop() {
                        // Wait for the specified pause duration.
                        setTimeout(() => {
                            // Rewind the video to the beginning.
                            video.currentTime = 0;
                            // Play the video again.
                            const loopPlayPromise = video.play();
                            if (loopPlayPromise !== undefined) {
                                loopPlayPromise.catch(() => {
                                    /* fail silently */
                                });
                            }
                        }, pauseMs); // Use pause duration from the options object.
                    });
                }
            }
        }
    };

    // Expose the autoplay and canplay functions.
    return {
        autoplay,
        canplay,
    };
};
